import { Autocomplete, Text } from "@mantine/core";
import { forwardRef, useCallback } from "react";
import { useFormContext, useController } from "react-hook-form";
import { useGetApi } from "src/hooks";

import { useInputProps } from "../hooks/useInputProps";
import dayjs from "dayjs";
import { DateInput } from "@mantine/dates";
import { toMantineDateString } from "src/utils/mantineDates";

interface IPatient {
  data: {
    firstname: string;
    surname: string;
    dob: string;
    position: string;
  };
  supid: string;
  value: string;
  label?: string;
  userid?: string;
}
interface IPatientInfoRow {
  text: string;
  id: string;
}
interface IPatientInfo {
  med_allergies?: IPatientInfoRow[];
  med_complaint?: IPatientInfoRow[];
  med_history?: IPatientInfoRow[];
  med_medications?: IPatientInfoRow[];
  med_oralintake?: IPatientInfoRow[];
}

interface Props {
  uuid: string;
  tag: string;
  value: string;
}

const tags = ["med_fname", "med_sname", "med_dob", "med_position"];

const getKey = (tag: string) => {
  switch (tag) {
    case "med_fname":
      return "firstname";
    case "med_sname":
      return "surname";
    case "med_dob":
      return "dob";
    case "med_position":
      return "position";
    default:
      return "firstname";
  }
};

const getPatientLabel = (patient: IPatient) => {
  const { position, firstname, surname, dob } = patient.data ?? {};
  const formattedDob =
    dob && dayjs(dob).isValid() ? dayjs(dob).format("DD MMM YYYY") : dob;
  return [position, firstname, surname, formattedDob].filter(Boolean).join(" ");
};

const getMedFieldInput = (medTag: string): HTMLInputElement | null => {
  const wrapper = document.querySelector(`div.${medTag}`);
  if (!wrapper) return null;

  if (medTag === "med_dob") {
    const sibling = wrapper.nextElementSibling;
    if (sibling instanceof HTMLInputElement) return sibling;
    const siblingInput = sibling?.querySelector("input");
    if (siblingInput instanceof HTMLInputElement) return siblingInput;
  }

  const input = wrapper.querySelector("input");
  return input instanceof HTMLInputElement ? input : null;
};

const MedBasicInput = ({ uuid, tag, value }: Props) => {
  const { control, setValue } = useFormContext();
  const { onChange, ...inputProps } = useInputProps(uuid, value ?? "");

  const { field } = useController({
    control,
    name: uuid,
    defaultValue: value ?? "",
  });

  const mapper = useCallback(
    (data: any) => {
      const seen = new Set<string>();
      const patients =
        data?.list
          ?.map((p: IPatient) => {
            const fieldValue = p?.data?.[getKey(tag)] ?? "";
            return {
              ...p,
              value: fieldValue,
              // Must match `value` — Mantine sets the input to `label` on select.
              label: fieldValue,
            };
          })
          .filter((p: IPatient) => {
            if (!p.supid) return false;
            const duplicate = seen.has(p.value);
            seen.add(p.value);
            return !duplicate;
          }) ?? [];
      return patients;
    },
    [tag],
  );

  const { data, refetch } = useGetApi<IPatient[]>({
    request: "get-supplement",
    body: { params: { tag } },
    fetchOnInit: false,
    mapper,
    cache: false,
  });

  const handleDateChange = useCallback(
    (date: string | null) => {
      const isoString = date ? dayjs(date).format("YYYY-MM-DD") : "";
      field.onChange(isoString);
    },
    [field],
  );

  const handleAutocompleteChange = useCallback(
    (text: string) => {
      onChange(text);
      void refetch({ params: { text } });
    },
    [onChange, refetch],
  );

  const handlePatientFilter = useCallback(
    ({
      options,
      search,
      limit,
    }: {
      options: IPatient[];
      search: string;
      limit: number;
    }) => {
      const parsedSearch = search.trim().toLowerCase();
      if (!parsedSearch) return options;

      const result: IPatient[] = [];
      for (const option of options) {
        if (result.length >= limit) break;
        if (getPatientLabel(option).toLowerCase().includes(parsedSearch)) {
          result.push(option);
        }
      }
      return result;
    },
    [],
  );

  const handleItemSubmit = useCallback(
    (submittedValue: string) => {
      const item = data?.find(patient => patient.value === submittedValue);
      if (!item) return;

      tags.forEach(t => {
        if (t === tag) return;
        const fieldName = getMedFieldInput(t)?.name;
        if (!fieldName) return;
        setValue(fieldName, item.data?.[getKey(t)] ?? "");
      });
    },
    [data, setValue, tag],
  );

  return tag === "med_dob" &&
    (field.value
      ? ["/", "T", "Z", "-"].some(c => field.value.includes(c)) &&
        dayjs(field.value).isValid()
      : true) ? (
    <DateInput
      {...inputProps}
      id={uuid}
      name={uuid}
      className={tag}
      value={toMantineDateString(field.value)}
      onChange={handleDateChange}
      valueFormat="DD MMM YYYY"
      styles={{ input: { textTransform: "uppercase" } }}
      popoverProps={{ withinPortal: true }}
    />
  ) : (
    <Autocomplete
      {...inputProps}
      renderOption={({ option }) => <AutoCompleteItem {...option} />}
      onChange={handleAutocompleteChange}
      onOptionSubmit={handleItemSubmit}
      filter={handlePatientFilter}
      data={data ?? []}
      className={tag}
      comboboxProps={{ withinPortal: true }}
      maxDropdownHeight={400}
      limit={25}
    />
  );
};

const AutoCompleteItem = forwardRef<HTMLDivElement, IPatient>(
  (patient, ref) => (
    <div ref={ref}>
      <Text size="sm">{getPatientLabel(patient)}</Text>
    </div>
  ),
);

export default MedBasicInput;
